import { useState } from 'react';
import { X, User, Euro, Wallet, TrendingUp, TrendingDown, CheckCircle, Trash2, Briefcase } from 'lucide-react';
import { formatCurrency } from '../utils/formatters';
import { calculatePersonBalance } from '../features/debts/debtLogic';
import DebtTransactionItem from './DebtTransactionItem';
import SalaryWizard from './SalaryWizard';

const PersonDetail = ({ 
  person, 
  debts = [], 
  incomes = [],
  wallets = [],
  onClose, 
  onEditDebt,
  onDeleteDebt,
  onShowDebtTransactionDetail,
  onResolveSituation,
  onDeletePerson,
  onSalaryReceived,
  onOpenSalaryWizard
}) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  const personTransactions = debts.filter(d => d.personName === person.name)
    .sort((a, b) => new Date(b.date) - new Date(a.date));
  
  const balance = calculatePersonBalance(debts, person.name);
  
  const getBalanceColor = (balance) => {
    if (balance > 0) {
      return 'text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/40';
    } else if (balance < 0) {
      return 'text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-900/40';
    }
    return 'text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-900/40';
  };

  const getBalanceIcon = (balance) => {
    if (balance > 0) {
      return <TrendingUp className="w-4 h-4" />;
    } else if (balance < 0) {
      return <TrendingDown className="w-4 h-4" />;
    }
    return <Euro className="w-4 h-4" />;
  };

  const getBalanceText = (balance) => {
    if (balance > 0) {
      return formatCurrency(balance);
    } else if (balance < 0) {
      return formatCurrency(Math.abs(balance));
    }
    return 'Saldo azzerato';
  };



  const getWalletName = (walletId) => {
    const wallet = wallets.find(w => w.id === walletId);
    return wallet ? wallet.name : 'Conto non trovato';
  };

  const handleResolveSituation = () => {
    if (balance === 0) {
      alert('Il saldo è già azzerato!');
      return;
    }
    onResolveSituation(person.name, balance);
  };

  const handleDeletePerson = () => {
    if (balance !== 0) {
      // Mostra messaggio di errore solo quando si tenta di eliminare
      alert('Impossibile eliminare una persona con la quale hai ancora un debito o un credito.');
      return;
    }
    setShowDeleteConfirm(true);
  };

  const confirmDeletePerson = () => {
    onDeletePerson(person.name);
    setShowDeleteConfirm(false);
    onClose();
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
        <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl max-w-md w-full animate-fade-in-up max-h-[85vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
          {/* Header */}
          <div className="bg-blue-600/90 backdrop-blur-sm text-white p-4 rounded-t-3xl">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <User className="w-5 h-5" />
                {person.name}
              </h2>
              <button
                onClick={onClose}
                className="text-white/80 hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto overflow-x-hidden">
            {/* Riepilogo saldo */}
            <div className="p-6 bg-gray-50 dark:bg-gray-700/30 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">Saldo Totale</p>
                  <p className={`text-2xl font-bold ${balance > 0 ? 'text-green-600' : balance < 0 ? 'text-red-600' : 'text-gray-900 dark:text-gray-100'}`}>
                    {formatCurrency(balance)}
                  </p>
                </div>
                <div className={`p-3 rounded-2xl ${balance > 0 ? 'bg-green-100 dark:bg-green-900/30 text-green-600' : balance < 0 ? 'bg-red-100 dark:bg-red-900/30 text-red-600' : 'bg-gray-100 dark:bg-gray-800 text-gray-500'}`}>
                  {balance > 0 ? <TrendingUp className="w-6 h-6" /> : balance < 0 ? <TrendingDown className="w-6 h-6" /> : <Euro className="w-6 h-6" />}
                </div>
              </div>
            </div>

            {/* Lista movimenti */}
            <div className="p-6">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
                <Euro className="w-4 h-4 text-gray-400" />
                Movimenti Recenti
              </h3>
              
              <div className="space-y-3">
                {personTransactions.length > 0 ? (
                  personTransactions.map(transaction => (
                    <DebtTransactionItem 
                      key={transaction.id}
                      transaction={transaction}
                      onEdit={onEditDebt}
                      onDelete={onDeleteDebt}
                      onShowDetail={onShowDebtTransactionDetail}
                      getWalletName={getWalletName}
                    />
                  ))
                ) : (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/20 rounded-2xl border-2 border-dashed border-gray-200 dark:border-gray-700">
                    Nessun movimento registrato.
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Footer con pulsanti */}
          <div className="p-6 border-t border-gray-200 dark:border-gray-700 space-y-3">
            {/* Stipendio Ricevuto — visibile solo se c'è un credito */}
            {balance > 0 && onSalaryReceived && (
              <button
                onClick={onOpenSalaryWizard}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl font-medium hover:from-emerald-700 hover:to-teal-700 transition-all duration-200 shadow-lg shadow-emerald-500/20"
              >
                <Briefcase className="w-4 h-4" />
                Stipendio Ricevuto
              </button>
            )}

            <button
              onClick={handleResolveSituation}
              disabled={balance === 0}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white rounded-xl font-medium hover:bg-green-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <CheckCircle className="w-4 h-4" />
              Risolvi Situazione
            </button>
            
            <button
              onClick={handleDeletePerson}
              disabled={balance !== 0}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-600 text-white rounded-xl font-medium hover:bg-red-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Trash2 className="w-4 h-4" />
              Elimina Persona
            </button>
          </div>
        </div>

        {/* Modal conferma eliminazione */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4" onClick={() => setShowDeleteConfirm(false)}>
            <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl max-w-sm w-full animate-fade-in-up" onClick={e => e.stopPropagation()}>
              <div className="p-6 space-y-4">
                <div className="text-center">
                  <Trash2 className="w-12 h-12 text-red-600 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                    Elimina Persona
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    Sei sicuro di voler eliminare tutti i movimenti di <strong>{person.name}</strong>?
                    Questa azione non può essere annullata.
                  </p>
                </div>
                
                <div className="flex gap-4 pt-4">
                  <button
                    onClick={() => setShowDeleteConfirm(false)}
                    className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-xl font-medium hover:bg-gray-300 dark:hover:bg-gray-600 transition-all duration-200"
                  >
                    Annulla
                  </button>
                  <button
                    onClick={confirmDeletePerson}
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-xl font-medium hover:bg-red-700 transition-all duration-200"
                  >
                    Elimina
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default PersonDetail;